import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface Vertical {
  id: string;
  name: string;
  icon: string;
  stat: string;
  statLabel: string;
  description: string;
  gradient: string;
  featured?: boolean;
}

const verticals: Vertical[] = [
  {
    id: 'auto-ai',
    name: 'Auto AI',
    icon: '🚗',
    stat: '2,400+',
    statLabel: 'OEM dealerships',
    description: 'AI products for automotive retail — built on the largest dealer management ecosystem in Latin America.',
    gradient: 'from-[#3B82F6]/10 to-[#1E40AF]/5',
    featured: true,
  },
  {
    id: 'retail-ai',
    name: 'Retail AI',
    icon: '🛍️',
    stat: '500+',
    statLabel: 'retail locations',
    description: 'AI products for retail operations, inventory optimization, and customer experience.',
    gradient: 'from-[#8B5CF6]/10 to-[#6D28D9]/5',
  },
  {
    id: 'hospitality-ai',
    name: 'Hospitality AI',
    icon: '🏨',
    stat: '200+',
    statLabel: 'properties',
    description: 'AI products for hotel and restaurant management, booking optimization, and guest experience.',
    gradient: 'from-[#F59E0B]/10 to-[#D97706]/5',
  },
  {
    id: 'transportation-ai',
    name: 'Transportation AI',
    icon: '🚛',
    stat: '1,000+',
    statLabel: 'fleet vehicles',
    description: 'AI products for fleet management, logistics optimization, and predictive maintenance.',
    gradient: 'from-[#10B981]/10 to-[#059669]/5',
  },
  {
    id: 'real-estate-ai',
    name: 'Real Estate AI',
    icon: '🏢',
    stat: '10K+',
    statLabel: 'units managed',
    description: 'AI products for property management, tenant experience, and real estate transactions.',
    gradient: 'from-[#EC4899]/10 to-[#DB2777]/5',
  },
];

const LabsVerticals: React.FC = () => {
  const handleClick = (id: string) => {
    if (id === 'auto-ai') {
      document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      document.getElementById('footer-cta')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="verticals" className="relative py-24 px-6 sm:px-8 lg:px-12 border-t border-[#1F1F2E]/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#3B82F6]/50 mb-3 block">
            Verticals
          </span>
          <h2 className="font-space-grotesk text-3xl sm:text-4xl font-bold text-white mb-3">
            Where We Build
          </h2>
          <p className="text-[#A1A1AA]/50 text-sm max-w-lg">
            Each vertical is powered by a portfolio company that owns the system of record — giving us distribution, data, and integration from day one.
          </p>
        </motion.div>

        {/* Featured card (Auto AI) — full width */}
        {verticals.filter(v => v.featured).map((v) => (
          <motion.button
            key={v.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onClick={() => handleClick(v.id)}
            className={`w-full text-left mb-6 group rounded-2xl border border-[#3B82F6]/20 bg-gradient-to-br ${v.gradient} p-8 sm:p-10 hover:border-[#3B82F6]/40 transition-all duration-300`}
          >
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{v.icon}</span>
                  <span className="text-[9px] px-2.5 py-0.5 rounded-full bg-[#3B82F6]/15 text-[#3B82F6] uppercase tracking-wider font-medium">
                    Featured
                  </span>
                </div>
                <h3 className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white mb-2">{v.name}</h3>
                <p className="text-[#A1A1AA]/60 text-sm leading-relaxed max-w-xl">{v.description}</p>
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <span className="text-3xl font-bold text-white">{v.stat}</span>
                <span className="text-[#A1A1AA]/40 text-xs block mt-1">{v.statLabel}</span>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#3B82F6] text-sm font-medium group-hover:gap-3 transition-all">
              Explore Auto AI Products
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.button>
        ))}

        {/* Other verticals — 2x2 grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          {verticals.filter(v => !v.featured).map((v, i) => (
            <motion.button
              key={v.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              onClick={() => handleClick(v.id)}
              className={`text-left group rounded-2xl border border-[#1F1F2E] bg-gradient-to-br ${v.gradient} p-6 sm:p-8 hover:border-[#1F1F2E]/80 transition-all duration-300`}
            >
              <span className="text-2xl mb-4 block">{v.icon}</span>
              <h3 className="font-space-grotesk text-lg font-bold text-white mb-1.5">{v.name}</h3>
              <p className="text-[#A1A1AA]/50 text-xs leading-relaxed mb-4">{v.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-bold text-lg">{v.stat}</span>
                  <span className="text-[#A1A1AA]/40 text-[10px] ml-2">{v.statLabel}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LabsVerticals;
